import { memo } from "react";
import { Draggable } from "react-beautiful-dnd";

import { TableRow } from "@mui/material";

import { LineDragInsertCopy } from "components/formFields/line/LineDragInsertCopy";
import { EditLineButton } from "components/utility/EditLineButton";

import { copyDown } from "services/utility/lineItems";

import { LineItem } from "views/PurchaseTransactions/LineItem";

// Memoized row component to prevent unnecessary re-renders
const MemoizedLineItem = memo(
  ({
    line,
    index,
    objectType,
    record,
    lineHandler,
    expandItemGroup,
    items,
    itemsCount,
    addItem,
    taxCodes,
    classes,
    classesCount,
    customers,
    jobWorkcenters,
    bins,
    location,
    lineItemsErrors,
    showAppliedTo,
    showBinSerialAndLot,
    showReceived,
    insertEmptyLine,
    isLastLine,
    lineReadOnly = false,
    nextLineReadOnly,
    setLineEdit,
    handleDeleteLine,
  }) => {
    return (
      <Draggable key={index} draggableId={`${index}`} index={index}>
        {(draggableProvided, snapshot) => {
          return (
            <TableRow
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              sx={{
                ...draggableProvided.draggableProps.style,
                position: "inherit",
                backgroundColor: snapshot.isDragging
                  ? "dragBackground"
                  : "none",
              }}
              data-testing={"line-" + index}
            >
              <LineDragInsertCopy
                draggableProvided={draggableProvided}
                snapshot={snapshot}
                insertEmptyLine={insertEmptyLine}
                lineNumber={line.lineNumber}
                onCopyDown={() => copyDown(line, lineHandler, objectType)}
                showCopyDown={!isLastLine}
                toggleLineEdit={setLineEdit}
                lineIndex={index}
                nextLineReadOnly={nextLineReadOnly}
              />
              <EditLineButton
                onClick={setLineEdit}
                lineIndex={index}
                recordId={record.id}
                lineReadOnly={lineReadOnly}
              />

              <LineItem
                objectType={objectType}
                record={record}
                line={line}
                lineHandler={lineHandler}
                expandItemGroup={expandItemGroup}
                items={items}
                itemsCount={itemsCount}
                addItem={addItem}
                taxCodes={taxCodes}
                classes={classes}
                classesCount={classesCount}
                customers={customers}
                jobWorkcenters={jobWorkcenters}
                bins={bins}
                location={location}
                lineItemsErrors={lineItemsErrors}
                draggableProvided={draggableProvided}
                draggableSnapshot={snapshot}
                showAppliedTo={showAppliedTo}
                showBinSerialAndLot={showBinSerialAndLot}
                showReceived={showReceived}
                lineReadOnly={lineReadOnly}
                handleDeleteLine={handleDeleteLine}
              />
            </TableRow>
          );
        }}
      </Draggable>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    return (
      prevProps.line === nextProps.line &&
      prevProps.index === nextProps.index &&
      prevProps.customers === nextProps.customers &&
      prevProps.taxCodes === nextProps.taxCodes &&
      prevProps.lineItemsErrors === nextProps.lineItemsErrors &&
      prevProps.isLastLine === nextProps.isLastLine &&
      prevProps.items === nextProps.items &&
      prevProps.classes === nextProps.classes &&
      prevProps.jobWorkcenters === nextProps.jobWorkcenters &&
      prevProps.bins === nextProps.bins &&
      prevProps.location === nextProps.location &&
      prevProps.record.autoSerialLotNumbering ===
        nextProps.record.autoSerialLotNumbering &&
      prevProps.lineReadOnly === nextProps.lineReadOnly &&
      prevProps.nextLineReadOnly === nextProps.nextLineReadOnly
    );
  }
);

// helps React DevTools and error messages show a readable name
MemoizedLineItem.displayName = "MemoizedLineItem";

export { MemoizedLineItem };
